import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { type SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH, cn } from "@/lib/utils";

import {
  CORRECTIVE_ACTION_SOURCES,
  correctiveActionSourceLabel,
  type CorrectiveActionSource,
} from "./correctiveActionsApi";
import { CreateQualityCaseModal } from "./CreateQualityCaseModal";
import {
  QUALITY_CASES_LIST_KEY,
  QUALITY_CASE_STATUSES,
  fetchQualityCases,
  qualityCaseLabel,
  qualityCaseStatusLabel,
  type QualityCase,
  type QualityCaseStatus,
} from "./casesApi";

const HELP_TEXT = [
  "A quality case is the dossier for a significant event: RMA, customer escalation, or a batch-wide finding.",
  "QC incidents stay the shop-floor capture for one device. Start an investigation on the case when you need saved comments",
  "and a final report across one or more devices. The CAR is a later closure snapshot, not the live workspace.",
].join(" ");

type OpenStatusFilter = QualityCaseStatus | "open_workflow";

const OPEN_WORKFLOW_STATUSES = new Set<QualityCaseStatus>([
  "open",
  "investigating",
  "actions_open",
  "pending_effectiveness",
]);

const STATUS_FILTER_OPTIONS: Array<{ key: OpenStatusFilter | "all"; label: string }> = [
  { key: "all", label: "All" },
  { key: "open_workflow", label: "Open workflow" },
  ...QUALITY_CASE_STATUSES.map((status) => ({
    key: status,
    label: qualityCaseStatusLabel(status),
  })),
];

type CaseSortKey = "id" | "title" | "status" | "owner" | "openedAt" | "incidentCount";

function statusTone(status: QualityCaseStatus): BadgeTone {
  switch (status) {
    case "closed":
      return "success";
    case "cancelled":
      return "neutral";
    case "investigating":
      return "info";
    case "pending_effectiveness":
      return "info";
    case "open":
      return "warning";
    default:
      return "warning";
  }
}

function formatDate(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const CASE_COLUMNS: HubDataTableColumn<QualityCase, CaseSortKey>[] = [
  {
    id: "id",
    header: "Case",
    sortKey: "id",
    overflow: "nowrap",
    sortValue: (row) => row.id,
    cell: (row) => (
      <Link className="font-medium text-sky-700 hover:underline" to={`/quality/cases/${row.id}`}>
        {qualityCaseLabel(row.id)}
      </Link>
    ),
  },
  {
    id: "title",
    header: "Title",
    sortKey: "title",
    sortValue: (row) => row.title,
    cell: (row) => row.title,
  },
  {
    id: "status",
    header: "Status",
    sortKey: "status",
    sortValue: (row) => row.status,
    cell: (row) => <Badge tone={statusTone(row.status)}>{qualityCaseStatusLabel(row.status)}</Badge>,
  },
  {
    id: "owner",
    header: "Owner",
    sortKey: "owner",
    sortValue: (row) => row.ownerDisplay ?? "",
    cell: (row) => row.ownerDisplay ?? EM_DASH,
  },
  {
    id: "incidentCount",
    header: "Incidents",
    sortKey: "incidentCount",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.incidentCount,
    cell: (row) => row.incidentCount,
  },
  {
    id: "openedAt",
    header: "Opened",
    sortKey: "openedAt",
    overflow: "nowrap",
    sortValue: (row) => row.openedAt,
    cell: (row) => formatDate(row.openedAt),
  },
];

function compareCases(left: QualityCase, right: QualityCase, key: CaseSortKey, dir: SortDirection): number {
  const direction = dir === "asc" ? 1 : -1;
  const leftOwner = left.ownerDisplay ?? "";
  const rightOwner = right.ownerDisplay ?? "";
  switch (key) {
    case "id":
      return (left.id - right.id) * direction;
    case "title":
      return left.title.localeCompare(right.title) * direction;
    case "status":
      return left.status.localeCompare(right.status) * direction;
    case "owner":
      return leftOwner.localeCompare(rightOwner) * direction;
    case "incidentCount":
      return (left.incidentCount - right.incidentCount) * direction;
    case "openedAt":
    default:
      return (new Date(left.openedAt).getTime() - new Date(right.openedAt).getTime()) * direction;
  }
}

export function QualityCasesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("quality");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<OpenStatusFilter | "all">("open_workflow");
  const [sourceFilter, setSourceFilter] = useState<CorrectiveActionSource | "all">("all");
  const [showCreate, setShowCreate] = useState(false);
  const [sortKey, setSortKey] = useState<CaseSortKey>("openedAt");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const listQuery = useQuery({
    queryKey: [...QUALITY_CASES_LIST_KEY, search, statusFilter, sourceFilter],
    queryFn: () =>
      fetchQualityCases({
        search: search.trim() || undefined,
        status:
          statusFilter === "all" || statusFilter === "open_workflow" ? undefined : statusFilter,
        sourceType: sourceFilter === "all" ? undefined : sourceFilter,
        limit: 200,
      }),
  });

  const rows = useMemo(() => {
    const loaded = listQuery.data?.rows ?? [];
    const filtered =
      statusFilter === "open_workflow"
        ? loaded.filter((row) => OPEN_WORKFLOW_STATUSES.has(row.status))
        : loaded;
    return [...filtered].sort((left, right) => compareCases(left, right, sortKey, sortDir));
  }, [listQuery.data?.rows, statusFilter, sortKey, sortDir]);

  const toggleSort = (key: CaseSortKey) => {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "id" || key === "openedAt" || key === "incidentCount" ? "desc" : "asc");
  };

  const filtersActive =
    search.trim() !== "" || statusFilter !== "all" || sourceFilter !== "all";

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={HELP_TEXT}>Quality cases</PageTitleWithHelp>
        {writesAllowed ? (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New case
          </Button>
        ) : null}
      </header>

      <CreateQualityCaseModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(detail) => {
          void queryClient.invalidateQueries({ queryKey: QUALITY_CASES_LIST_KEY });
          showToast({ tone: "success", message: `Opened ${qualityCaseLabel(detail.case.id)}.` });
          void navigate(`/quality/cases/${detail.case.id}`);
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <Label htmlFor="quality-case-search">Search quality cases</Label>
            <Input
              id="quality-case-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by case number, title, or product family"
            />
          </div>
          <div>
            <Label htmlFor="quality-case-source-filter">Source</Label>
            <select
              id="quality-case-source-filter"
              className={cn(
                "mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
                "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
              )}
              value={sourceFilter}
              onChange={(event) => setSourceFilter(event.target.value as CorrectiveActionSource | "all")}
            >
              <option value="all">All sources</option>
              {CORRECTIVE_ACTION_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {correctiveActionSourceLabel(source)}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <p className="mb-2 text-xs font-medium text-slate-600">Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTER_OPTIONS.map((option) => (
                <Button
                  key={option.key}
                  size="sm"
                  variant={statusFilter === option.key ? "primary" : "secondary"}
                  onClick={() => setStatusFilter(option.key)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={
          listQuery.isLoading
            ? "Loading quality cases…"
            : `${rows.length} quality case${rows.length === 1 ? "" : "s"}`
        }
        columns={CASE_COLUMNS}
        rows={rows}
        rowKey={(row) => row.id}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        loading={listQuery.isLoading}
        loadingMessage="Loading quality cases…"
        error={listQuery.isError ? (listQuery.error as Error).message : null}
        empty="No quality cases yet."
        isFiltered={filtersActive}
        filteredEmpty="No quality cases match these filters."
      />
    </div>
  );
}
